import { Avatar, Grid, Typography, makeStyles } from "@material-ui/core";
import {
  AccessTime,
  Cancel,
  CheckCircle,
  ChevronRight,
  Clear,
  Done,
  MoreVert,
  Share,
} from "@material-ui/icons";
import { App, Events, Workouts } from "app-interfaces";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch } from "react-router-dom";
import Popup from "reactjs-popup";
import { v4 as uuid } from "uuid";

import upcomingIcon from "../../assets/clock.svg";
import { useStateValue } from "../../config/AppContext";
import { extractTimeFromDateString } from "../../date-utils";
import { Action, SessionForms } from "../../types/StaticTypes";
import { isDashboardEvent } from "../../types/utils";
import { SessionEvent } from "../Client/ClientSessions";
import { LoadingModal } from "../Modals";
import ListClientsModal from "../Modals/ListClientsModal";
import SessionFormModal, { SessionFormView } from "../Modals/SessionFormModal";
import { hasHappenedBeforeThisInstant } from "../Utilities/CustomLuxonUtils";
import Icons from "../Utilities/Icons";

export const sharedStyles = {
  root: {
    flexGrow: 1,
  },
  container: {
    "&.break": {
      width: "100%",
      margin: 0,
      padding: 0,
      height: 39,
      "& h5": {
        padding: 10,
      },
    },
    "&.add": {
      width: "100%",
      margin: 0,
      padding: 0,
      height: 85,
      "& h5": {
        padding: 20,
      },
    },
    "&.blocked": {
      width: "100%",
      margin: 0,
      padding: 0,
      height: 85,
      "& h5": {
        padding: 20,
        background: "repeating-linear-gradient(45deg, #d3d3d3, #ffffff 4px)",
        cursor: "default",
      },
    },
    "&.lunch": {
      width: "100%",
      margin: 0,
      padding: 0,
      height: 85,
      "& h5": {
        padding: 20,
        background: "repeating-linear-gradient(45deg, #d3d3d3, #ffffff 4px)",
      },
    },
    "& h5": {
      fontSize: 14,
      textAlign: "center",
      textTransform: "uppercase",
      color: "var(--heading)",
      "& span": {
        display: "block",
        fontWeight: 500,
        color: "var(--text-primary)",
        cursor: "pointer",
      },
    },
  },
  clientProfileAvatar: {
    float: "right",
    height: 38,
    width: 38,
    marginTop: -20,
  },
  sessionInfo: {
    display: "inline-block",
    width: "100%",
    "& .session-client-info": {
      position: "relative",
      "& .client-name": {
        width: "60%",
        fontSize: 16,
        textTransform: "capitalize",
      },
      "& .client-profile": {
        cursor: "pointer",
        "& .client-list-number": {
          display: "block",
          position: "absolute",
          cursor: "pointer",
          top: 4,
          right: 0,
          height: 38,
          width: 38,
          fontSize: 16,
          fontWeight: "bold",
          textAlign: "center",
          color: "#ffffff",
          borderRadius: "50%",
          padding: "8px 3px",
          backgroundColor: "#33333394",
        },
      },
    },
    "& .session-workout-info": {
      fontSize: 14,
      width: "60%",
      "& .MuiTypography-body2": { display: "block" },
    },
  },
  paper: {
    height: 107,
    padding: "8px 12px",
    cursor: "pointer",
    "& .session-booking": {
      marginBottom: -5,
      "& .session-calendar": {
        "& svg": {
          marginTop: -3,
          marginLeft: -5,
          height: 15,
        },
      },
      "& span": {
        fontSize: 14,
        textTransform: "none",
      },
      "& span.dot": {
        display: "inline-block",
        height: 4,
        width: 4,
        margin: "3px 5px",
        borderRadius: "50%",
        backgroundColor: "#333333",
      },
    },
    "& .session-status": {
      "& svg": {
        float: "right",
        fill: "var(--text-heading)",
        marginRight: 8,
      },
    },
    "& .session-status.completed": {
      "& svg": {
        fill: "var(--text-primary)",
      },
    },
    "& .session-options": {
      "& li": {
        fontSize: 14,
        fontWeight: "bold",
        padding: "5px 0",
        "& svg": {
          float: "none",
          height: 24,
          width: 24,
        },
      },
      "& svg": {
        float: "right",
        marginRight: 8,
        cursor: "pointer",
      },
    },
  },
};
// @ts-ignore
export const useStyles = makeStyles(sharedStyles);

/**
 * Unclear exactly what this does. Seems to show a "normal" session (not lunch, blocked, etc),
 * although the plural form indicates something else.
 *
 * See prop docs for more
 */
export const SessionListings: React.FC<SessionListingsProps> = ({ event, dispatch }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { ic_user } = Icons;
  const session_length = isDashboardEvent(event) && event.session_length;
  const { Methods } = useStateValue() as { Methods: App.Methods };

  const [showClients, setShowClients] = React.useState(false);
  const [view, setView] = useState<SessionFormView>(SessionFormView.DEFAULT);
  const [modal, setModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [session, setSession] = useState<Events.SessionFull | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const formId = e.currentTarget.id;
    const params: {
      sessionWorkoutId?: string;
      sessionWorkoutName?: string;
      sessionWorkoutDesc?: string;
      sessionWorkoutDuration?: string;
      sessionWorkoutNotes?: string;
      sessionWorkoutExercises?: string;

      sessionNotes?: string;
      duration?: string;
      from?: string;
      to?: string;
    } = {};
    formData.forEach((v, k) => Object.assign(params, { [k]: v }));
    switch (formId) {
      case SessionForms[SessionForms.SESSIONDETAILS]: {
        if (!params.sessionNotes || !session || !event.id || loading) break;
        const sessionCustomers = (session.customer || []).map((el) => el.id);
        const payload: Events.IEvents = {
          id: event.id,
          start: event.start,
          end: event.end,
          type: event.type,
          name: event.name,
          session: {
            id: session.id,
            customer_id: sessionCustomers,
            workout_id: params.sessionWorkoutId,
            notes: params.sessionNotes,
            state: session.state,
            state_reason: session.state_reason,
            state_notes: session.state_notes,
            state_time: session.state_time,
            is_booked: session.is_booked,
          },
        };
        dispatch({
          type: "update-session",
          payload: {
            params: payload,
            callback: () => setSession(null),
          },
        });
        break;
      }
      case SessionForms[SessionForms.WORKOUTS]: {
        if (loading) break;

        // todo: get rid of all the !
        const workout: Workouts.IWorkout = {
          id: params.sessionWorkoutId!,
          name: params.sessionWorkoutName!,
          description: params.sessionWorkoutDesc!,
          duration: parseInt(params.sessionWorkoutDuration!, 10),
          notes: params.sessionWorkoutNotes,
          exercise_super_set:
            params.sessionWorkoutExercises && JSON.parse(params.sessionWorkoutExercises),
          is_deleted: false,
        };
        const payload = {
          id: event.id,
          start: event.start, // does not seem used by updateScheduleWorkout(id, workout)
          workout: workout,
        };
        dispatch({
          type: "update-workout",
          payload: {
            params: payload,
            callback: () => setSession(null),
          },
        });
        break;
      }
      default:
        throw new Error(t("Unhandled case type"));
    }
    setModal(true);
    setLoading(false);
  };

  const handleWorkout = (params: Workouts.IWorkout) => {
    if (loading) return;

    params.id = uuid();
    const payload = {
      id: event.id,
      start: event.start,
      workout: params,
    };

    setModal(false);
    setLoading(true);

    Methods.updateScheduledWorkout(payload).then(() =>
      Methods.getEventDetails(event.id).then(({ event }) => {
        if (event.session) {
          setSession(event.session);
        }
        setLoading(false);
      })
    );
  };
  const createPopupActions = (close: () => void) => {
    const callback = () => {
      close();
      setSession(null);
    };

    const eventStartHasAlreadyHappened = event && hasHappenedBeforeThisInstant(event.start);
    const pastIncompleteSession =
      session &&
      session.state !== "COMPLETED" &&
      session.state !== "CANCELLED" &&
      eventStartHasAlreadyHappened;
    const pastIncompleteEvent =
      event &&
      event.session?.state !== "COMPLETED" &&
      event.session?.state !== "CANCELLED" &&
      eventStartHasAlreadyHappened;

    return (
      <ul style={{ marginBottom: "0" }}>
        {session
          ? pastIncompleteSession && (
              <li
                onClick={() =>
                  dispatch({
                    type: "complete-session",
                    payload: {
                      event: {
                        ...event,
                        session: session,
                      },
                      callback,
                    },
                  })
                }
              >
                <Done />
                {t("Complete Session")}
              </li>
            )
          : pastIncompleteEvent && (
              <li
                onClick={() =>
                  dispatch({
                    type: "complete-session",
                    payload: {
                      event: {
                        ...event,
                        session: event.session,
                      },
                      callback,
                    },
                  })
                }
              >
                <Done />
                {t("Complete Session")}
              </li>
            )}
        <li
          onClick={() => {
            close();
            setView(SessionFormView.SHARE);
            setModal(true);
          }}
        >
          <Share />
          {t("Share session")}
        </li>
        {session
          ? session.state !== "COMPLETED" &&
            session.state !== "CANCELLED" && (
              <li
                onClick={() => {
                  close();
                  setView(SessionFormView.CANCEL);
                  setModal(true);
                }}
              >
                <Clear />
                {t("Cancel session")}
              </li>
            )
          : event.session?.state !== "COMPLETED" &&
            event.session?.state !== "CANCELLED" && (
              <li
                onClick={() => {
                  close();
                  setView(SessionFormView.CANCEL);
                  setModal(true);
                }}
              >
                <Clear />
                {t("Cancel session")}
              </li>
            )}
      </ul>
    );
  };

  React.useEffect(() => {
    // TODO: try to clean up the state handling. Way too many potential NPEs

    if (!loading && !session && event.session) {
      setLoading(true);
      Methods.getEventDetails(event.id).then(({ event }) => {
        if (event.session) {
          setSession(event.session);
        }
        setLoading(false);
      });
    }
    if (session && event.session?.id !== session.id) {
      return setSession(null);
    }
  }, [event, Methods, loading, session]);

  const renderModals = () => (
    <>
      <SessionFormModal
        show={modal}
        event={event}
        view={view}
        setView={setView}
        session={session}
        time={{
          start: extractTimeFromDateString(event.start),
          end: extractTimeFromDateString(event.end),
          datetime: new Date(event.start),
        }}
        dispatch={dispatch}
        handleWorkout={handleWorkout}
        onSubmit={handleSubmit}
        onHide={() => {
          setSession(null);
          setModal(false);
          setView(SessionFormView.DEFAULT);
        }}
      />
      <LoadingModal show={loading} />
      <ListClientsModal
        clientsList={session ? session.customer : []}
        show={showClients}
        onHide={() => setShowClients(false)}
      />
    </>
  );

  // const isEditable = isDashboardEvent(event); // && event.is_editable;
  return (
    <Switch>
      <Route path={"/clients/"}>
        <Grid container spacing={3} direction="row" onClick={() => setModal(true)}>
          <Grid container item className={classes.sessionInfo}>
            <Grid item xs={8}>
              {session?.workout ? (
                <SessionWorkoutInfo workoutName={session.workout.name} />
              ) : (
                <WorkoutNotAdded />
              )}
            </Grid>
            <Grid item xs={4}>
              {session &&
                (session.state === "COMPLETED" ? (
                  <div className="session-status completed" style={{ color: "var(--primary)" }}>
                    <CheckCircle />
                    {t("Completed")}
                  </div>
                ) : session.state === "CANCELLED" ? (
                  <div className="session-status cancelled" style={{ color: "var(--danger)" }}>
                    <Cancel />
                    {t("Cancelled")}
                  </div>
                ) : (
                  <div className="session-status upcoming" style={{ color: "var(--warning)" }}>
                    <img src={upcomingIcon} style={{ marginRight: "10px" }} alt="upcoming" />
                    {t("Upcoming")}
                  </div>
                ))}
            </Grid>
          </Grid>
          <Grid item className="session-booking">
            <span className="session-calendar">
              <AccessTime />
            </span>

            <span>
              {extractTimeFromDateString(event.start)} - {extractTimeFromDateString(event.end)}
            </span>
            <span className="session-options">
              <ChevronRight />
            </span>
          </Grid>
        </Grid>
        {renderModals()}
      </Route>
      <Route path={["/", "/schedules"]}>
        <Grid container spacing={3} direction="column" onClick={() => setModal(true)}>
          <Grid item className="session-booking">
            <span className="session-calendar">
              <AccessTime />
            </span>
            <span>
              {extractTimeFromDateString(event.start)} - {extractTimeFromDateString(event.end)}
            </span>
            {session_length && <SessionDuration sessionLength={session_length} />}
            {isDashboardEvent(event) && event.session_type === "OPTIONS" ? (
              <span
                className="session-options"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Popup
                  on="click"
                  mouseEnterDelay={0}
                  repositionOnResize={true}
                  position="bottom right"
                  trigger={<MoreVert />}
                  arrow={true}
                  contentStyle={{
                    textAlign: "left",
                    width: "240px",
                    padding: 10,
                    cursor: "pointer",
                    borderRadius: 5,
                    backgroundColor: "#ffffff",
                    color: "var(--text-primary)",
                  }}
                >
                  {createPopupActions}
                </Popup>
              </span>
            ) : (
              <span
                className={
                  session && session.state === "COMPLETED"
                    ? "session-status completed"
                    : "session-status"
                }
              >
                <CheckCircle />
              </span>
            )}
          </Grid>
          <Grid item className={classes.sessionInfo}>
            <div className="session-client-info">
              <Typography variant="body1" noWrap className="client-name">
                {session && session.customer.length > 0 && session.customer[0].first_name}{" "}
                {session && session.customer.length > 0 && session.customer[0].last_name}
              </Typography>
              <span className="client-profile">
                <Avatar
                  alt="profile"
                  className={classes.clientProfileAvatar}
                  src={
                    session && session.customer.length > 0 && session.customer[0].photo
                      ? session.customer[0].photo.small
                      : ic_user
                  }
                />
                {session && session.customer.length > 1 && (
                  <span
                    className="client-list-number"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      return setShowClients(true);
                    }}
                  >
                    +{session.customer.length - 1}
                  </span>
                )}
              </span>
            </div>
            {session?.workout ? (
              <SessionWorkoutInfo workoutName={session.workout.name} />
            ) : (
              <WorkoutNotAdded />
            )}
          </Grid>
        </Grid>
        {renderModals()}
      </Route>
    </Switch>
  );
};

interface SessionListingsProps {
  /**
   * Unclear how this relates to the session
   */
  event: Events.IAgenda | SessionEvent;
  /**
   * Is this the same as the session object on the event?
   */
  session?: Events.ISession;
  dispatch: React.Dispatch<Action>;
}

const SessionDuration: React.FC<{ sessionLength: number }> = ({ sessionLength }) => {
  const { t } = useTranslation();

  const interval = {
    hours: Math.floor(sessionLength / 60),
    mins: sessionLength % 60,
  };

  return (
    <>
      <span className="dot" />
      <span style={{ color: "var(--heading)", textTransform: "uppercase" }}>
        {interval.hours > 0 && `${interval.hours} ${t(interval.hours > 1 ? "Hrs" : "Hr")} `}
        {`${interval.mins} ${t("mins")}`}
      </span>
    </>
  );
};

const SessionWorkoutInfo = ({ workoutName }: { workoutName: string }) => (
  <div className="session-workout-info">
    <Typography variant="body2" component="span" noWrap>
      {workoutName}
    </Typography>
  </div>
);

const WorkoutNotAdded = () => {
  const { t } = useTranslation();
  return (
    <div className="session-workout-info" style={{ color: "var(--danger)" }}>
      {t("Workout not added")}
    </div>
  );
};
